import {
    RequestService,
    RequestServiceResponse,
} from '@tcc-online/request-service';
import ProjectQuestionnaire from '../types/project/ProjectQuestionnaire';
import Section from '../types/project/360/Section';
import Subsection from '../types/project/360/Subsection';
import Question from '../types/project/360/Question';
import ProjectTranslation from '../types/project/ProjectTranslation';
import QuestionTranslation from '../types/project/360/QuestionTranslation';
import { type AdminProjectType } from '../constants/AdminProjectTypes';
import {
    type AutoTranslateQuestionsResult,
    type AutoTranslateTranslationsResult,
} from '../types/AutoTranslateResult';

const Admin360QuestionnaireService = {
    getQuestionnaires,
    getQuestionnaire,
    createQuestionnaire,
    updateQuestionnaire,
    deleteQuestionnaire,
    copyQuestionnaire,
    getSections,
    getSection,
    createSection,
    updateSection,
    getSubsections,
    getSubsection,
    createSubsection,
    updateSubsection,
    getQuestions,
    getQuestion,
    createQuestion,
    updateQuestion,
    deleteQuestion,
    getTranslations,
    saveTranslations,
    saveTranslation,
    addLanguage,
    removeLanguage,
    getQuestionTranslations,
    updateQuestionTranslations,
    updateQuestionTranslation,
    autoTranslate,
    autoTranslateQuestions,
};

const BASE_URL = '/admin/products';
const createUrl = (productType: AdminProjectType) =>
    `${BASE_URL}/${productType}/questionnaires`;

async function getQuestionnaires(payload: {
    productType: AdminProjectType;
    page?: number;
    search?: string;
}): Promise<RequestServiceResponse<ProjectQuestionnaire[]>> {
    const { productType, page = 1, search } = payload;
    return await RequestService.get<any>(`${createUrl(productType)}`, {
        page,
        search,
    });
}

async function getQuestionnaire(payload: {
    productType: AdminProjectType;
    questionnaireId: number;
}): Promise<RequestServiceResponse<ProjectQuestionnaire>> {
    const { productType, questionnaireId } = payload;
    return await RequestService.get<ProjectQuestionnaire>(
        `${createUrl(productType)}/${questionnaireId}`,
    );
}

async function createQuestionnaire(payload: {
    productType: AdminProjectType;
    name?: string;
    primaryLanguage?: string;
}): Promise<RequestServiceResponse<ProjectQuestionnaire>> {
    const { productType, name, primaryLanguage } = payload;
    return await RequestService.post<
        { name?: string; primaryLanguage?: string },
        ProjectQuestionnaire
    >(`${createUrl(productType)}`, {
        name,
        primaryLanguage,
    });
}

async function updateQuestionnaire(payload: {
    productType: AdminProjectType;
    questionnaireId: number;
    name?: string;
    primaryLanguage?: string;
    timeToFill?: string;
    description?: string;
    config?: {
        freeCommentText: string;
        openingText: string;
    };
    scale?: { id: number };
}): Promise<RequestServiceResponse<ProjectQuestionnaire[]>> {
    const {
        productType,
        questionnaireId,
        name,
        primaryLanguage,
        timeToFill,
        config,
        description,
        scale,
    } = payload;
    return await RequestService.post<
        Record<string, unknown>,
        ProjectQuestionnaire[]
    >(
        `${createUrl(productType)}/${questionnaireId}`,
        {
            name,
            primaryLanguage,
            timeToFill,
            config,
            description,
            scale,
        },
        { allowMultiRequest: true },
    );
}

async function deleteQuestionnaire(payload: {
    productType: AdminProjectType;
    questionnaireId: number;
}): Promise<RequestServiceResponse<void>> {
    const { productType, questionnaireId } = payload;
    return await RequestService.delete(
        `${createUrl(productType)}/${questionnaireId}`,
    );
}

async function copyQuestionnaire(payload: {
    productType: AdminProjectType;
    questionnaireId: number;
    name: string;
}): Promise<RequestServiceResponse<ProjectQuestionnaire>> {
    const { productType, questionnaireId, name } = payload;
    return await RequestService.post<{ name: string }, ProjectQuestionnaire>(
        `${createUrl(productType)}/${questionnaireId}/copy`,
        { name },
    );
}

const getSectionsUrl = (
    productType: AdminProjectType,
    questionnaireId: number,
): string => `${createUrl(productType)}/${questionnaireId}/sections`;

async function getSections(payload: {
    productType: AdminProjectType;
    questionnaireId: number;
    locale?: string;
}) {
    const { productType, questionnaireId, locale } = payload;
    return await RequestService.get<Section[]>(
        getSectionsUrl(productType, questionnaireId),
        {},
        locale ? { headers: { Language: locale } } : {},
    );
}

async function getSection(payload: {
    productType: AdminProjectType;
    questionnaireId: number;
    sectionId: number;
}) {
    const { productType, questionnaireId, sectionId } = payload;
    return await RequestService.get<Section>(
        `${getSectionsUrl(productType, questionnaireId)}/${sectionId}`,
    );
}

async function createSection(payload: {
    productType: AdminProjectType;
    questionnaireId: number;
    name: string;
}) {
    const { productType, questionnaireId, name } = payload;
    return await RequestService.post<{ name: string }, Section>(
        getSectionsUrl(productType, questionnaireId),
        {
            name,
        },
    );
}

async function updateSection(payload: {
    productType: AdminProjectType;
    questionnaireId: number;
    sectionId: number;
    name?: string;
    order?: number;
}) {
    const { productType, questionnaireId, sectionId, name, order } = payload;
    return await RequestService.put<{ name?: string; order?: number }, Section>(
        `${getSectionsUrl(productType, questionnaireId)}/${sectionId}`,
        { name, order },
    );
}

const getSubsectionsUrl = (
    productType: AdminProjectType,
    questionnaireId: number,
    sectionId: number,
): string =>
    `${getSectionsUrl(productType, questionnaireId)}/${sectionId}/subsections`;

async function getSubsections(payload: {
    productType: AdminProjectType;
    questionnaireId: number;
    sectionId: number;
}) {
    const { productType, questionnaireId, sectionId } = payload;
    return await RequestService.get<Subsection[]>(
        getSubsectionsUrl(productType, questionnaireId, sectionId),
    );
}

async function getSubsection(payload: {
    productType: AdminProjectType;
    questionnaireId: number;
    sectionId: number;
    subsectionId: number;
}) {
    const { productType, questionnaireId, sectionId, subsectionId } = payload;
    return await RequestService.get<Subsection>(
        `${getSubsectionsUrl(productType, questionnaireId, sectionId)}/${subsectionId}`,
    );
}

async function createSubsection(payload: {
    productType: AdminProjectType;
    questionnaireId: number;
    sectionId: number;
    name: string;
}) {
    const { productType, questionnaireId, sectionId, name } = payload;
    return await RequestService.post<{ name: string }, Subsection>(
        getSubsectionsUrl(productType, questionnaireId, sectionId),
        {
            name,
        },
    );
}

async function updateSubsection(payload: {
    productType: AdminProjectType;
    questionnaireId: number;
    sectionId: number;
    subsectionId: number;
    name?: string;
    order?: number;
}) {
    const {
        productType,
        questionnaireId,
        sectionId,
        subsectionId,
        name,
        order,
    } = payload;
    return await RequestService.put<
        { name?: string; order?: number },
        Subsection
    >(
        `${getSubsectionsUrl(productType, questionnaireId, sectionId)}/${subsectionId}`,
        {
            name,
            order,
        },
    );
}

const getQuestionsUrl = (
    productType: AdminProjectType,
    questionnaireId: number,
    sectionId: number,
    subsectionId: number,
): string =>
    `${getSubsectionsUrl(productType, questionnaireId, sectionId)}/${subsectionId}/questions`;

async function getQuestions(payload: {
    productType: AdminProjectType;
    questionnaireId: number;
    sectionId: number;
    subsectionId: number;
}) {
    const { productType, questionnaireId, sectionId, subsectionId } = payload;
    return await RequestService.get<Question[]>(
        getQuestionsUrl(productType, questionnaireId, sectionId, subsectionId),
    );
}

async function getQuestion(payload: {
    productType: AdminProjectType;
    questionnaireId: number;
    sectionId: number;
    subsectionId: number;
    questionId: number;
}) {
    const {
        productType,
        questionnaireId,
        sectionId,
        subsectionId,
        questionId,
    } = payload;
    return await RequestService.get<Question>(
        `${getQuestionsUrl(productType, questionnaireId, sectionId, subsectionId)}/${questionId}`,
    );
}

type QuestionType = 0 | 1;

export interface QuestionDataPayload {
    text: string;
    type: QuestionType;
}

export interface QuestionPayload extends QuestionDataPayload {
    productType: AdminProjectType;
    questionnaireId: number;
    sectionId: number;
    subsectionId: number;
}

async function createQuestion(payload: QuestionPayload) {
    const {
        productType,
        questionnaireId,
        sectionId,
        subsectionId,
        text,
        type,
    } = payload;
    return await RequestService.post<QuestionDataPayload, Question>(
        getQuestionsUrl(productType, questionnaireId, sectionId, subsectionId),
        {
            text,
            type,
        },
    );
}

async function updateQuestion(payload: {
    productType: AdminProjectType;
    questionnaireId: number;
    sectionId: number;
    subsectionId: number;
    questionId: number;
    text?: string;
    order?: number;
    type?: QuestionType;
}) {
    const {
        productType,
        questionnaireId,
        sectionId,
        subsectionId,
        questionId,
        text,
        order,
        type,
    } = payload;
    return await RequestService.patch<
        { text?: string; order?: number; type?: QuestionType },
        Question
    >(
        `${getQuestionsUrl(productType, questionnaireId, sectionId, subsectionId)}/${questionId}`,
        { text, order, type },
    );
}

async function deleteQuestion(payload: {
    productType: AdminProjectType;
    questionnaireId: number;
    sectionId: number;
    subsectionId: number;
    questionId: number;
}) {
    const {
        productType,
        questionnaireId,
        sectionId,
        subsectionId,
        questionId,
    } = payload;
    return await RequestService.delete(
        `${getQuestionsUrl(productType, questionnaireId, sectionId, subsectionId)}/${questionId}`,
    );
}

const getTranslationsUrl = (
    productType: AdminProjectType,
    questionnaireId: number,
    locale: string,
    translationId?: number,
): string =>
    `${createUrl(productType)}/${questionnaireId}/translations${
        translationId !== null && translationId !== undefined
            ? `/${translationId}`
            : ''
    }?locale=${locale}`;

async function getTranslations(payload: {
    productType: AdminProjectType;
    questionnaireId: number;
    locale: string;
}) {
    const { productType, questionnaireId, locale } = payload;
    return await RequestService.get<ProjectTranslation[]>(
        getTranslationsUrl(productType, questionnaireId, locale),
    );
}

async function addLanguage(payload: {
    productType: AdminProjectType;
    questionnaireId: number;
    locale: string;
    locales: string[];
}): Promise<RequestServiceResponse<ProjectQuestionnaire>> {
    const { productType, questionnaireId, locale, locales } = payload;
    return await RequestService.patch<
        { languages: string[] },
        ProjectQuestionnaire
    >(`${createUrl(productType)}/${questionnaireId}`, {
        languages: [...locales, locale],
    });
}

async function removeLanguage(payload: {
    productType: AdminProjectType;
    questionnaireId: number;
    locale: string;
    locales: string[];
}): Promise<RequestServiceResponse<ProjectQuestionnaire>> {
    const { productType, questionnaireId, locale, locales } = payload;
    return await RequestService.patch<
        { languages: string[] },
        ProjectQuestionnaire
    >(`${createUrl(productType)}/${questionnaireId}`, {
        languages: locales.filter((l) => l !== locale),
    });
}

async function saveTranslations(payload: {
    productType: AdminProjectType;
    questionnaireId: number;
    locale: string;
    translations: ProjectTranslation[];
}): Promise<RequestServiceResponse<unknown>> {
    const { productType, questionnaireId, locale, translations } = payload;
    return await RequestService.patch<unknown>(
        getTranslationsUrl(productType, questionnaireId, locale),
        translations,
    );
}

async function saveTranslation(payload: {
    productType: AdminProjectType;
    questionnaireId: number;
    locale: string;
    translation: ProjectTranslation;
}): Promise<RequestServiceResponse<unknown>> {
    const { productType, questionnaireId, locale, translation } = payload;
    const { id, value } = translation;
    return await RequestService.patch<unknown>(
        getTranslationsUrl(productType, questionnaireId, locale, id),
        { value },
    );
}

const getQuestionTranslationsUrl = (
    productType: AdminProjectType,
    questionnaireId: number,
    locale: string,
    translationId?: number,
): string =>
    `${createUrl(productType)}/${questionnaireId}/question-translations${
        translationId !== null && translationId !== undefined
            ? `/${translationId}`
            : ''
    }?locale=${locale}`;

async function getQuestionTranslations(payload: {
    productType: AdminProjectType;
    questionnaireId: number;
    locale: string;
}) {
    const { productType, questionnaireId, locale } = payload;
    return await RequestService.get<QuestionTranslation[]>(
        getQuestionTranslationsUrl(productType, questionnaireId, locale),
    );
}

async function updateQuestionTranslation(payload: {
    productType: AdminProjectType;
    questionnaireId: number;
    locale: string;
    translation: QuestionTranslation;
}): Promise<RequestServiceResponse<unknown>> {
    const { productType, questionnaireId, locale, translation } = payload;
    const { id, text, texts } = translation;
    return await RequestService.patch<unknown>(
        getQuestionTranslationsUrl(productType, questionnaireId, locale, id),
        { text, texts },
    );
}

async function updateQuestionTranslations(payload: {
    productType: AdminProjectType;
    questionnaireId: number;
    locale: string;
    translations: QuestionTranslation[];
}): Promise<RequestServiceResponse<unknown>> {
    const { productType, questionnaireId, locale, translations } = payload;
    return await RequestService.patch<unknown>(
        getQuestionTranslationsUrl(productType, questionnaireId, locale),
        translations,
    );
}

async function autoTranslate(payload: {
    productType: AdminProjectType;
    questionnaireId: number;
    locale: string;
}): Promise<RequestServiceResponse<AutoTranslateTranslationsResult>> {
    const { productType, questionnaireId, locale } = payload;
    return await RequestService.post<
        undefined,
        AutoTranslateTranslationsResult
    >(
        `${createUrl(productType)}/${questionnaireId}/translations/auto-translate?locale=${locale}`,
    );
}

async function autoTranslateQuestions(payload: {
    productType: AdminProjectType;
    questionnaireId: number;
    locale: string;
}): Promise<RequestServiceResponse<AutoTranslateQuestionsResult>> {
    const { productType, questionnaireId, locale } = payload;
    return await RequestService.post<undefined, AutoTranslateQuestionsResult>(
        `${createUrl(productType)}/${questionnaireId}/question-translations/auto-translate?locale=${locale}`,
    );
}

export default Admin360QuestionnaireService;
